import { apiFetch } from '../auth/apiFetch.ts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// Stored operational mode (RobotRepository.STATUSES). NOT connectivity —
// that's `liveness` below. 'offline' is a liveness value, never a status.
export type RobotStatus = 'idle' | 'active' | 'charging' | 'error' | 'maintenance';
export const STATUSES: readonly RobotStatus[] = [
  'idle', 'active', 'charging', 'error', 'maintenance',
];

// Derived connectivity from telemetry recency (server-side): whether the robot
// is reachable right now, independent of its operational mode. 'provisioning'
// means registered but no record has ever arrived (never phoned home).
export type Liveness = 'provisioning' | 'live' | 'backfilling' | 'stale' | 'offline';

export interface Robot {
  robot_id: string;
  name: string | null;
  model: string;
  morphology: string;
  morphology_version_id: number | null;
  status: RobotStatus;
  liveness: Liveness;
  battery_pct: number | null;
  firmware: string | null;
  lat: number | null;
  lon: number | null;
  heading_deg: number | null;
  last_seen: string | null;
  last_backfill_at: string | null;
  cert_serial: string | null;
  cert_not_after: string | null;
  enrolled_at: string | null;
  enrolled_by: string | null;
  command_profile: Record<string, unknown> | null;
  active: boolean;
  meta: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface RobotsFilters {
  status?: RobotStatus;
  activeOnly?: boolean;
}

async function fetchRobots(filters: RobotsFilters): Promise<Robot[]> {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.activeOnly) params.set('active_only', 'true');
  const qs = params.toString();
  const r = await apiFetch(`/api/robots${qs ? `?${qs}` : ''}`);
  if (!r.ok) throw new Error(`status ${r.status}`);
  const body = (await r.json()) as { robots?: Robot[] };
  return body.robots ?? [];
}

export function useRobots(filters: RobotsFilters = {}) {
  return useQuery({
    queryKey: ['robots', filters] as const,
    queryFn: () => fetchRobots(filters),
    // Live fleet view — poll every 5s.
    refetchInterval: 5_000,
  });
}

export const MORPHOLOGIES = ['rover', 'koko', 'wukong', 'jetrover'] as const;
export type Morphology = (typeof MORPHOLOGIES)[number];

export interface NewRobotInput {
  robot_id: string;
  name?: string | undefined;
  morphology?: Morphology | undefined;
  model?: string | undefined;
}

// Register a robot, then refresh the fleet list. Surfaces the API's validation
// message (e.g. bad robot_id slug, 409 already exists) as the thrown error.
export function useCreateRobot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: NewRobotInput): Promise<Robot> => {
      const r = await apiFetch('/api/robots', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!r.ok) {
        const detail = await r.json().catch(() => null);
        throw new Error(errorText(detail) || `status ${r.status}`);
      }
      return (await r.json()) as Robot;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['robots'] });
    },
  });
}

export interface RobotPatch {
  name?: string | null;
  model?: string;
  // Sending a DIFFERENT morphology repins the robot to that morphology's provisional
  // '001' -- a revision belongs to a morphology, so the old pin cannot survive the
  // move. Re-sending the current one changes nothing.
  morphology?: Morphology;
  status?: RobotStatus;
  firmware?: string | null;
  active?: boolean;
}

// Partial-update a robot (PATCH). Invalidates both the detail and the list.
export function useUpdateRobot(robotId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: RobotPatch): Promise<Robot> => {
      const r = await apiFetch(`/api/robots/${encodeURIComponent(robotId)}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (!r.ok) {
        const detail = await r.json().catch(() => null);
        throw new Error(errorText(detail) || `status ${r.status}`);
      }
      return (await r.json()) as Robot;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['robot', robotId] });
      void qc.invalidateQueries({ queryKey: ['robots'] });
      // A morphology change repins the revision AND changes which revisions are
      // available, so the cached revision list is stale either way.
      void qc.invalidateQueries({ queryKey: ['robot-revisions', robotId] });
    },
  });
}

// Deregister a robot (DELETE → 204). Invalidates the fleet list.
export function useDeleteRobot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (robotId: string): Promise<void> => {
      const r = await apiFetch(`/api/robots/${encodeURIComponent(robotId)}`, {
        method: 'DELETE',
      });
      if (!r.ok && r.status !== 204) {
        const detail = await r.json().catch(() => null);
        throw new Error(errorText(detail) || `status ${r.status}`);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['robots'] });
    },
  });
}

export interface EnrollmentToken {
  robot_id: string;
  enrollment_token: string;
  expires_at: string;
  note: string;
}

// Mint a one-time enrollment token for a robot (PRD-0001 §5 step 1). The raw
// token is returned once; the server stores only its hash. Re-minting replaces
// any prior unused token.
export function useMintEnrollmentToken(robotId: string) {
  return useMutation({
    mutationFn: async (): Promise<EnrollmentToken> => {
      const r = await apiFetch(
        `/api/robots/${encodeURIComponent(robotId)}/enrollment-token`,
        { method: 'POST' },
      );
      if (!r.ok) {
        const detail = await r.json().catch(() => null);
        throw new Error(errorText(detail) || `status ${r.status}`);
      }
      return (await r.json()) as EnrollmentToken;
    },
  });
}

// FastAPI returns {detail: "..."} (HTTPException) or {detail: [{msg, loc}, ...]}
// (422 validation). Flatten either into one line.
function errorText(detail: unknown): string {
  const d = (detail as { detail?: unknown } | null)?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((e) => (e as { msg?: string }).msg).filter(Boolean).join('; ');
  return '';
}
